/**
 * RiskMatrix5x5 — Matriz de riesgo 5×5 (Probabilidad × Severidad) interactiva.
 *
 * Recibe listas de IPER y Bow-Tie y renderiza:
 *  • Grid 5×5 heatmap (verde → amarillo → naranja → rojo por P×S)
 *  • Burbujas IPER (azul, cuadrante sup-izq) y Bow-Tie (rojo, inf-der)
 *  • Al hacer click sobre una burbuja se abre un panel lateral con los ítems
 *    que caen en esa celda para drill-down inmediato.
 */
import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Warning, ShieldCheck } from "@phosphor-icons/react";

const cellColor = (p, s) => {
  const score = p * s;
  if (score >= 20) return "#FCA5A5";
  if (score >= 12) return "#FDBA74";
  if (score >= 6) return "#FEF08A";
  return "#BBF7D0";
};

const bucketIper = (rows) => {
  const b = {};
  for (const r of rows || []) {
    const p = Math.max(1, Math.min(5, Number(r.probability) || 0));
    const s = Math.max(1, Math.min(5, Number(r.severity) || 0));
    if (!p || !s) continue;
    const k = `${p}-${s}`;
    if (!b[k]) b[k] = [];
    b[k].push(r);
  }
  return b;
};

const bucketBowtie = (rows) => {
  const b = {};
  for (const r of rows || []) {
    let p = Number(r.probability) || 0;
    let s = Number(r.severity) || 0;
    if (!p) p = Math.min(5, Math.max(2, (r.threats || []).length));
    if (!s) s = Math.min(5, Math.max(2, (r.consequences || []).length));
    p = Math.max(1, Math.min(5, p));
    s = Math.max(1, Math.min(5, s));
    const k = `${p}-${s}`;
    if (!b[k]) b[k] = [];
    b[k].push(r);
  }
  return b;
};

const bubbleR = (n) => Math.min(22, Math.max(10, 8 + 3 * n));

export default function RiskMatrix5x5({ iperRows = [], bowtieRows = [] }) {
  const [drill, setDrill] = useState(null); // { kind, p, s, items }

  const iperBuckets = useMemo(() => bucketIper(iperRows), [iperRows]);
  const btBuckets = useMemo(() => bucketBowtie(bowtieRows), [bowtieRows]);

  const cellSize = 76;
  const gridSize = cellSize * 5;
  const padX = 44;
  const padY = 32;
  const totalW = padX + gridSize + 8;
  const totalH = padY + gridSize + 44;

  const cells = [];
  for (let p = 1; p <= 5; p++) {
    for (let s = 1; s <= 5; s++) {
      const x = padX + (s - 1) * cellSize;
      // p=5 arriba, p=1 abajo (svg Y invertida)
      const y = padY + (5 - p) * cellSize;
      cells.push(
        <g key={`c-${p}-${s}`}>
          <rect x={x} y={y} width={cellSize} height={cellSize}
                fill={cellColor(p, s)} stroke="white" strokeWidth="1" />
          <text x={x + cellSize - 4} y={y + cellSize - 3} fontSize="9"
                textAnchor="end" fontWeight="700" fill="#334155">
            {p * s}
          </text>
        </g>,
      );
    }
  }

  const bubbles = [];
  Object.entries(iperBuckets).forEach(([k, items]) => {
    const [p, s] = k.split("-").map(Number);
    const cx = padX + (s - 1) * cellSize + cellSize * 0.34;
    const cy = padY + (5 - p) * cellSize + cellSize * 0.36;
    const r = bubbleR(items.length);
    bubbles.push(
      <g key={`i-${k}`} className="cursor-pointer transition-transform hover:scale-110"
         onClick={() => setDrill({ kind: "iper", p, s, items })}
         data-testid={`risk-cell-iper-${p}-${s}`}>
        <circle cx={cx} cy={cy} r={r} fill="#1E40AF" stroke="white" strokeWidth="1.5" />
        <text x={cx} y={cy + 4} fontSize="11" fontWeight="800" fill="white" textAnchor="middle">
          {items.length}
        </text>
        <title>{`IPER · ${items.length} evento(s) — Probabilidad ${p} × Severidad ${s}`}</title>
      </g>,
    );
  });
  Object.entries(btBuckets).forEach(([k, items]) => {
    const [p, s] = k.split("-").map(Number);
    const cx = padX + (s - 1) * cellSize + cellSize * 0.66;
    const cy = padY + (5 - p) * cellSize + cellSize * 0.64;
    const r = bubbleR(items.length);
    bubbles.push(
      <g key={`b-${k}`} className="cursor-pointer transition-transform hover:scale-110"
         onClick={() => setDrill({ kind: "bowtie", p, s, items })}
         data-testid={`risk-cell-bowtie-${p}-${s}`}>
        <circle cx={cx} cy={cy} r={r} fill="#B91C1C" stroke="white" strokeWidth="1.5" />
        <text x={cx} y={cy + 4} fontSize="11" fontWeight="800" fill="white" textAnchor="middle">
          {items.length}
        </text>
        <title>{`Bow-Tie · ${items.length} evento(s) — Probabilidad ${p} × Severidad ${s}`}</title>
      </g>,
    );
  });

  const xLabels = [1, 2, 3, 4, 5].map((s) => (
    <text key={s} x={padX + (s - 0.5) * cellSize} y={padY - 8} fontSize="11"
          fontWeight="700" fill="#0F172A" textAnchor="middle">{s}</text>
  ));
  const yLabels = [1, 2, 3, 4, 5].map((p) => (
    <text key={p} x={padX - 8} y={padY + (5 - p) * cellSize + cellSize / 2 + 4}
          fontSize="11" fontWeight="700" fill="#0F172A" textAnchor="end">{p}</text>
  ));

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="risk-matrix-5x5">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="font-heading font-bold text-slate-900">Matriz de Riesgo 5×5 · Interactiva</h3>
        <span className="text-[11px] text-slate-500">Click en una burbuja para ver el detalle</span>
      </div>
      <svg viewBox={`0 0 ${totalW} ${totalH}`} className="w-full max-w-[520px] mx-auto block">
        <text x={padX + gridSize / 2} y={totalH - 6} fontSize="10" fontWeight="700"
              fill="#0F172A" textAnchor="middle">
          SEVERIDAD ▶ (1 leve · 5 catastrófica)
        </text>
        <text x={12} y={padY + gridSize / 2} fontSize="10" fontWeight="700"
              fill="#0F172A" textAnchor="middle" transform={`rotate(-90 12 ${padY + gridSize / 2})`}>
          PROBABILIDAD ▲
        </text>
        {xLabels}
        {yLabels}
        {cells}
        {bubbles}
      </svg>
      <div className="flex flex-wrap justify-center gap-4 text-[11px] mt-2 text-slate-600">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-[#1E40AF]" /> IPER (inicial)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-[#B91C1C]" /> Bow-Tie</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#BBF7D0] border border-slate-300" /> Bajo (1-5)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#FEF08A] border border-slate-300" /> Medio (6-11)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#FDBA74] border border-slate-300" /> Alto (12-19)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#FCA5A5] border border-slate-300" /> Crítico (20-25)</span>
      </div>

      <Dialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent className="bg-white max-w-2xl" data-testid="risk-drill-dialog">
          {drill && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-heading">
                  {drill.kind === "iper" ? <ShieldCheck className="text-blue-800" /> : <Warning className="text-red-700" />}
                  {drill.kind === "iper" ? "IPER" : "Bow-Tie"} · Celda P{drill.p}×S{drill.s}
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full`} style={{ background: cellColor(drill.p, drill.s) }}>
                    Score {drill.p * drill.s}
                  </span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                <p className="text-xs text-slate-500">
                  {drill.items.length} evento(s) en esta celda:
                </p>
                {drill.items.map((it, i) => (
                  <div key={it.id || i} className="border border-slate-200 rounded-md p-3 hover:bg-slate-50">
                    <p className="font-semibold text-sm text-slate-900" data-testid={`drill-item-${i}`}>
                      {it.hazard || it.top_event || it.description || it.title || `Ítem ${i + 1}`}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1 text-[11px] text-slate-500 mt-1">
                      {it.area && <span><b>Área:</b> {it.area}</span>}
                      {it.process && <span><b>Proceso:</b> {it.process}</span>}
                      {it.owner && <span><b>Responsable:</b> {it.owner}</span>}
                      {(it.threats?.length) ? <span><b>Amenazas:</b> {it.threats.length}</span> : null}
                      {(it.consequences?.length) ? <span><b>Consecuencias:</b> {it.consequences.length}</span> : null}
                      {it.risk_level && <span><b>Nivel:</b> {it.risk_level}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
